import React, { SVGProps } from "react";

import { ReactComponent as BookIcon } from "../../../assets/svg/book.svg";
import { ReactComponent as PortfolioIcon } from "../../../assets/svg/portfolio.svg";
import { ReactComponent as SurveyIcon } from "../../../assets/svg/survey-student.svg";
import TeacherHeader from "../../../organisms/teacher/TeacherHeader";
import {
  IPackage,
  IDetailsSurvey,
  ISurveyAnswers,
  ISimpleTeachingMaterial,
  ILessonByTeachingMaterial,
  IPortfolio,
  IBreadcrumbsLink,
} from "../../../state";
import { classDataPath } from "../../../teacherStore";
import Tabs from "../../../atoms/Tabs";
import Button from "../../../atoms/Button";
import { IClassDataScreenType } from "../../../pages/teacher/ClassDataContents";
import Portfolio from "../../../organisms/teacher/Portfolio";
import ClassDataSurvey from "../../../organisms/teacher/ClassDataSurvey";
import QuestionList from "../../../organisms/teacher/QuestionList";
import Dropdown from "../../../atoms/Dropdown";
import { IStudent } from "../../../state";
import StudentInfoLabel from "../../../atoms/StudentInfoLabel";

import styles from "./ClassDataContents.module.scss";

export type Props = {
  calling: boolean;
  materialCalling: boolean;
  links: IBreadcrumbsLink[];
  student?: IStudent;
  students: IStudent[];
  nameClass: string;
  surveyEnabled: boolean;
  screenType: IClassDataScreenType;
  packages: IPackage[];
  details_survey: IDetailsSurvey[] | null;
  survey_answers: ISurveyAnswers[];
  survey_answers_per_teaching_material: ISurveyAnswers[];
  teaching_materials: ISimpleTeachingMaterial[];
  lessonsByTeachingMaterial?: ILessonByTeachingMaterial[];
  portfolio: IPortfolio | null;
  temporaryToken: string | null;
  teachingMaterialId: number;
  onBack: () => void;
  fetchSurveyFunc: (material_id: number) => void;
  fetchDetailsSurveyFunc: (package_id: number) => void;
  onTeachingMaterialOfPortfolioChange: (material_id: number) => void;
  onTeachingMaterialOfQuestionChange: (material_id: number) => void;
  onDownloadFile: () => void;
  onClearTemporaryToken: () => void;
  onChangeStudent: (student: IStudent) => void;
  onResetStudentPassword: () => void;
};

const ClassDataContents: React.FC<Props> = ({
  calling,
  materialCalling,
  links,
  student,
  students,
  nameClass,
  surveyEnabled,
  screenType,
  details_survey,
  packages,
  survey_answers,
  survey_answers_per_teaching_material,
  teaching_materials,
  lessonsByTeachingMaterial,
  portfolio,
  temporaryToken,
  teachingMaterialId,
  onBack,
  fetchSurveyFunc,
  fetchDetailsSurveyFunc,
  onTeachingMaterialOfPortfolioChange,
  onTeachingMaterialOfQuestionChange,
  onDownloadFile,
  onClearTemporaryToken,
  onChangeStudent,
  onResetStudentPassword,
}) => {
  const portfolioTeachingMaterials = React.useMemo(
    () => teaching_materials.filter(material => material.portfolio_type),
    [teaching_materials],
  );

  const tabsHeader: {
    title: string;
    icon: SVGProps<SVGSVGElement>;
    link: string;
  }[] = React.useMemo(() => {
    if (!student) return [];
    return [
      {
        title: "教材回答一覧",
        icon: <BookIcon />,
        link: `${classDataPath}/${student.class_id}/student/${student.id}/response`,
        state: { nameClass, students },
      },
      ...(portfolioTeachingMaterials.length
        ? [
            {
              title: "生徒帳票",
              icon: <PortfolioIcon />,
              link: `${classDataPath}/${student.class_id}/student/${student.id}/portfolio`,
              state: { nameClass, students },
            },
          ]
        : []),
      ...(surveyEnabled
        ? [
            {
              title: "事前/事後アンケート確認",
              icon: <SurveyIcon />,
              link: `${classDataPath}/${student.class_id}/student/${student.id}/survey`,
              state: { nameClass, students },
            },
          ]
        : []),
    ];
  }, [
    nameClass,
    portfolioTeachingMaterials.length,
    student,
    students,
    surveyEnabled,
  ]);

  const renderScreen = React.useCallback(() => {
    if (!student) return null;
    switch (screenType) {
      case "response":
        return (
          <QuestionList
            key={student.id}
            teaching_materials={teaching_materials}
            listLessons={lessonsByTeachingMaterial}
            teachingMaterialId={teachingMaterialId}
            onChangeTeachingMaterial={onTeachingMaterialOfQuestionChange}
          />
        );
      case "portfolio":
        return (
          <Portfolio
            key={student.id}
            calling={calling}
            portfolio={portfolio}
            student={student}
            temporaryToken={temporaryToken}
            teaching_materials={portfolioTeachingMaterials}
            onTeachingMaterialChange={onTeachingMaterialOfPortfolioChange}
            onDownloadFile={onDownloadFile}
            onClearTemporaryToken={onClearTemporaryToken}
          />
        );
      case "survey":
        return surveyEnabled ? (
          <ClassDataSurvey
            key={student.id}
            details_survey={details_survey}
            packages={packages}
            survey_answers={survey_answers}
            survey_answers_per_teaching_material={
              survey_answers_per_teaching_material
            }
            nameClass={nameClass}
            student={student}
            fetchSurveyFunc={fetchSurveyFunc}
            fetchDetailsSurveyFunc={fetchDetailsSurveyFunc}
          />
        ) : null;
      default:
        return null;
    }
  }, [
    screenType,
    teaching_materials,
    portfolioTeachingMaterials,
    lessonsByTeachingMaterial,
    calling,
    portfolio,
    temporaryToken,
    surveyEnabled,
    details_survey,
    packages,
    survey_answers,
    survey_answers_per_teaching_material,
    nameClass,
    student,
    teachingMaterialId,
    fetchSurveyFunc,
    fetchDetailsSurveyFunc,
    onTeachingMaterialOfPortfolioChange,
    onTeachingMaterialOfQuestionChange,
    onDownloadFile,
    onClearTemporaryToken,
  ]);

  return (
    <>
      <TeacherHeader links={links} />
      <div className={styles.wrapHeader}>
        <div className={styles.btnGroup}>
          <div className={styles.btnBack} onClick={onBack}>
            くクラス選択に戻る
          </div>
          <div>
            <Button
              name="パスワードリセット"
              variant="outline"
              onClick={onResetStudentPassword}
              className={styles.resetBtn}
            />
            {student && student.temporary_key && (
              <p className={styles.newPassword}>
                仮パスワード:{student.temporary_key}
              </p>
            )}
          </div>
        </div>
        <Dropdown
          className="mt-4"
          defaultValue={
            student
              ? {
                  value: student.id,
                  label: <StudentInfoLabel student={student} />,
                }
              : undefined
          }
          options={students.map(s => ({
            value: s.id,
            label: <StudentInfoLabel student={s} />,
          }))}
          onChange={student_id => {
            const anotherStudent = students.find(s => s.id === student_id)!;
            onChangeStudent(anotherStudent);
          }}
        />
      </div>
      {!materialCalling && (
        <Tabs listTabsTitle={tabsHeader}>{renderScreen()}</Tabs>
      )}
    </>
  );
};
export default ClassDataContents;
